/**
 * Messages that never represent a defect we can act on. Passed to `ignoreErrors`
 * so the SDK drops them before an event is built, and re-checked in `beforeSend`
 * because wrapper errors (SignalR, Auth0) hide the real cause in a nested cause
 * chain that `ignoreErrors` does not walk.
 */
export const IGNORED_ERROR_MESSAGES: readonly (string | RegExp)[] = [
  // Transport failures. Surfaced to the user as a snackbar and retried by react-query;
  // an API outage is caught by backend monitoring, not by the browser SDK.
  // Narrowed: `ignoreErrors` drops the event SDK-side, before beforeSend's chunk-load
  // exemption can run, and Chrome reports a stale chunk as "Failed to fetch dynamically
  // imported module".
  /Failed to fetch(?! dynamically imported module)/,
  'Load failed',
  'NetworkError',
  'ERR_NETWORK',
  'ERR_INTERNET_DISCONNECTED',
  'Network request failed',
  'The network connection was lost',
  'The Internet connection appears to be offline',
  'signal is aborted without reason',
  /^AbortError/,

  // SignalR reconnects on its own via withAutomaticReconnect().
  "Cannot send data if the connection is not in the 'Connected' State",
  'Server returned an error on close',
  'Server timeout elapsed without receiving a message from the server',
  'Failed to complete negotiation with the server',
  'Failed to start the transport',
  'No Connection with that ID',
  /WebSocket (closed|failed)/,

  // Auth0 redirect flow. Each of these ends in a re-login or the VerifyEmail screen.
  'Login required',
  'login_required',
  'Consent required',
  'email_not_verified',
  'Get access token failed',
  'Invalid state',

  // Third-party SDKs that already fail closed.
  'LaunchDarklyFlagFetchError',

  // Browser extensions and crawlers, no frame of ours in the stack.
  'Script error',
  'ResizeObserver loop',
  'Non-Error promise rejection captured with value: undefined',
  /^Object Not Found Matching Id/,
]

export const DENIED_URLS: readonly RegExp[] = [
  /extensions\//i,
  /^chrome:\/\//i,
  /^chrome-extension:\/\//i,
  /^moz-extension:\/\//i,
  /^safari-(web-)?extension:\/\//i,
  /googletagmanager\.com/i,
  /widget\.intercom\.io/i,
  /js\.intercomcdn\.com/i,
]

const CHUNK_LOAD_MESSAGES: readonly (string | RegExp)[] = [
  'error loading dynamically imported module',
  'Importing a module script failed',
  'Failed to fetch dynamically imported module',
  /Loading chunk \d+ failed/,
]

const matches = (message: string, pattern: string | RegExp): boolean =>
  typeof pattern === 'string' ? message.includes(pattern) : pattern.test(message)

export const isIgnoredMessage = (message: string): boolean =>
  IGNORED_ERROR_MESSAGES.some(pattern => matches(message, pattern))

export const isChunkLoadMessage = (message: string): boolean =>
  CHUNK_LOAD_MESSAGES.some(pattern => matches(message, pattern))
